import type { HttpMethod } from '@seans/contracts';
import { ApiError, errorFromResponse } from './errors.ts';

export type QueryValue = string | number | boolean | undefined;

export interface HttpRequest {
  readonly method: HttpMethod;
  readonly path: string;
  readonly query?: Readonly<Record<string, QueryValue>> | undefined;
  readonly json?: unknown;
  readonly formData?: FormData | undefined;
  readonly signal?: AbortSignal | undefined;
  /** Overrides the transport default, e.g. for file uploads. */
  readonly timeoutMs?: number | undefined;
}

export interface HttpResponse {
  readonly status: number;
  /** Parsed JSON body, or null for empty bodies. */
  readonly body: unknown;
}

/** Port the API client depends on; the browser adapter below is the only production implementation. */
export type HttpTransport = (request: HttpRequest) => Promise<HttpResponse>;

export interface FetchTransportOptions {
  /** Same-origin by default: cookies are HttpOnly and travel automatically. */
  readonly baseUrl?: string;
  readonly timeoutMs: number;
}

const toQueryString = (query: HttpRequest['query']): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
};

const readBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const isAbort = (error: unknown): boolean => error instanceof DOMException && error.name === 'AbortError';
const isTimeout = (error: unknown): boolean => error instanceof DOMException && error.name === 'TimeoutError';

export const createFetchTransport = ({ baseUrl = '', timeoutMs }: FetchTransportOptions): HttpTransport => {
  return async (request) => {
    const timeout = AbortSignal.timeout(request.timeoutMs ?? timeoutMs);
    const signal = request.signal ? AbortSignal.any([request.signal, timeout]) : timeout;
    const headers: Record<string, string> = { Accept: 'application/json' };
    let body: BodyInit | undefined;
    if (request.formData) {
      body = request.formData;
    } else if (request.json !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(request.json);
    }
    let response: Response;
    try {
      response = await fetch(`${baseUrl}${request.path}${toQueryString(request.query)}`, {
        method: request.method,
        headers,
        body: body ?? null,
        credentials: 'same-origin',
        signal,
      });
    } catch (error) {
      // A caller-initiated abort is not a failure; let it propagate untouched.
      if (isAbort(error) && request.signal?.aborted) throw error;
      if (isTimeout(error) || timeout.aborted) {
        throw new ApiError('timeout', `${request.method} ${request.path} timed out`, { cause: error });
      }
      throw new ApiError('network', `${request.method} ${request.path} failed`, { cause: error });
    }
    const parsed = await readBody(response);
    if (!response.ok) throw errorFromResponse(response.status, parsed);
    return { status: response.status, body: parsed };
  };
};
